import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  ArrowLeft,
  Calendar,
  Clock,
  ThumbsUp,
  MessageCircle,
  Eye,
} from "lucide-react";
// import { blogs } from '../data/mock';
import { useData } from "@/contexts/DataContext";
import DOMPurify from "dompurify";
import ProfileInfo from "@/components/ui/ProfileInfo";
import ShareComponent from "@/components/ui/ShareComponent";
import ArticleActions from "@/components/ui/ArticleActions";
import SubscribeNewsletter from "@/components/ui/SubscribeNewsletter";
import NewsletterCard from "@/components/ui/NewsletterCard";
import ArticleSkeleton from "@/components/ui/ArticleSkeleton";

const BlogDetail = () => {
  const { id } = useParams();
  const { getBlogById, blogs } = useData();
  const [blog, setBlog] = useState(null);

  useEffect(() => {
    const loadBlog = async () => {
      if (id) {
        const response = await getBlogById(id);
        setBlog(response);
      }
    };
    loadBlog();
  }, [id, getBlogById]);
  // if (!blog) return <p className="text-center py-20">Loading...</p>;
  if (!blog) return <ArticleSkeleton />;

  const relatedBlogs = blogs
    .filter(
      (b) => b._id !== blog._id && b.tags.some((tag) => blog.tags.includes(tag))
    )
    .slice(0, 3);

  return (
    <div className="min-h-screen py-12">
      <div className="container mx-auto px-4">
        {/* Back Navigation */}
        <div className="mb-8">
          <Button variant="ghost" asChild>
            <Link to="/blog">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Blog
            </Link>
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Main Content */}
          <div className="lg:col-span-3 space-y-8">
            {/* Hero Image */}
            {blog.images?.length > 0 && (
              <div className="aspect-video rounded-xl overflow-hidden w-full">
                <img
                  src={blog.images[0].url || blog.images[0]}
                  alt={blog.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Article Header */}
            <div className="space-y-4">
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>{new Date(blog.createdAt).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>{blog.readTime}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Eye className="w-4 h-4" />
                  <span>{Math.floor(Math.random() * 1000) + 100} views</span>
                </div>
              </div>

              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
                {blog.title}
              </h1>

              <p className="text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed">
                {blog.excerpt}
              </p>

              <div className="flex flex-wrap gap-2">
                {blog.tags.map((tag, index) => (
                  <Badge key={index} variant="outline">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Article Content */}
            {/* <div className="prose prose-lg max-w-none text-muted-foreground">
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(blog.content),
                }}
              />
            </div> */}
            {/* <div className="prose prose-lg dark:prose-invert prose-headings:font-bold prose-a:text-blue-500 prose-img:rounded-xl max-w-none">
  <div
    className="leading-8 text-gray-800 dark:text-gray-300"
    dangerouslySetInnerHTML={{
      __html: DOMPurify.sanitize(blog.content),
    }}
  />
</div> */}
            {/* Article Content Wrapper */}
            <div className="prose prose-lg dark:prose-invert max-w-none 
    prose-headings:font-bold prose-headings:tracking-tight 
    prose-p:leading-8 prose-p:text-justify 
    prose-a:text-blue-500 hover:prose-a:text-blue-600 
    prose-strong:text-foreground prose-code:text-pink-500">

              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(blog.content),
                  FORBID_ATTR: ['style'], // Ye line style attributes ko hata degi

                }}
              />
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 py-8 border-t border-b">
              <ArticleActions />
              <ShareComponent blog={blog} />
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            <ProfileInfo />
            {/* Related Blogs */}
            {relatedBlogs.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-4">Related Articles</h3>
                  <div className="space-y-4">
                    {relatedBlogs.map((rb) => (
                      <Link
                        key={rb._id}
                        to={`/blog/${rb._id}`}
                        className="block p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex gap-3 items-start">
                          <img
                            src={rb.images?.[0]?.url || rb.images?.[0]}
                            alt={rb.title}
                            className="w-12 h-12 sm:w-16 sm:h-16 rounded object-cover flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-sm truncate">
                              {rb.title}
                            </h4>
                            <p className="text-xs text-muted-foreground mt-1">
                              {rb.readTime}
                            </p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <NewsletterCard />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogDetail;
